import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
} from "react-router";
import type { Route } from "./+types/root";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { queryClient } from "@/lib/queryClient";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SessionExpiredModal } from "@/components/errors/SessionExpiredModal";
import "@fontsource-variable/geist";
import "@fontsource-variable/geist-mono";
import "./index.css";

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
        <script src="/theme.js"></script>
        <script src="/env.js"></script>
        <Meta />
        <Links />
      </head>
      <body>
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-(--radius-sm) focus:bg-(--primary) focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-(--primary-foreground)"
        >
          Skip to main content
        </a>
        <TooltipProvider delayDuration={300}>
          <QueryClientProvider client={queryClient}>
            {children}
            <SessionExpiredModal />
            <Toaster position="bottom-right" richColors closeButton />
          </QueryClientProvider>
        </TooltipProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return <Outlet />;
}

export function HydrateFallback() {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-3 bg-(--background)">
      <img
        src="/icon.svg"
        alt="Centrali"
        width={32}
        height={32}
        className="animate-pulse rounded-[6px]"
      />
      <p className="text-sm text-(--foreground-muted)">Loading...</p>
    </div>
  );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  if (isRouteErrorResponse(error)) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 bg-(--background) p-8">
        <h1 className="text-lg font-semibold text-(--foreground)">{error.status}</h1>
        <p className="text-sm text-(--foreground-muted)">{error.statusText || "Page not found"}</p>
        <a
          href="/"
          className="mt-2 text-sm font-medium text-(--primary) hover:underline"
        >
          Back to dashboard
        </a>
      </div>
    );
  }
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-3 bg-(--background) p-8">
      <h1 className="text-lg font-semibold text-(--foreground)">Something went wrong</h1>
      <p className="text-sm text-(--foreground-muted)">Please try refreshing the page.</p>
      <a
        href="/"
        className="mt-2 text-sm font-medium text-(--primary) hover:underline"
      >
        Back to dashboard
      </a>
    </div>
  );
}
